<script setup lang="ts">
import { computed } from 'vue'
import formBaseTableRender from './form-base-table-render.vue'
import formBaseTableExpandRender from './form-base-table-expand-render.vue'
import { get } from 'lodash-unified'

import type { FormBaseColumnsItem } from './form-base'

defineOptions({
  name: 'ElFormBaseTableColumn',
})

const props = defineProps({
  column: Object as any,
  columnsData: {
    type: Array<any>,
    default: () => [],
  },
})

const emit = defineEmits({
  'click-info': (columns: any, row: any) => columns && row,
})

const handleHidden = computed(() => {
  if (typeof props.column.hidden === 'boolean') {
    return props.column.hidden
  }
  return true
})

const handleClick = (columns: FormBaseColumnsItem, row: any) => {
  emit('click-info', columns, row)
}

const getLink = computed(() => {
  if (props.column.isLink || props.column.isDetails) {
    return 'form-base__link__text'
  }
  return ''
})

const isFn = (val: any, row: any, flag = true) => {
  if (typeof val === 'boolean') {
    return val
  }
  if (typeof val === 'string') {
    return val
  }
  if (typeof val === 'function') {
    return val(row)
  }
  return flag
}

const toFormat = (props: any, row: any) => {
  if (row[props.column.prop]) {
    const formatter = isFn(props.column.dateFormatter, row, false)
    const date = formatDate(row[props.column.prop], formatter)
    if (date) {
      return date
    }
    return row[props.column.prop]
  }
  return row[props.column.prop]
}

function formatDate(date: any, fmt = 'yyyy-mm-dd hh:MM:ss') {
  if (!date) {
    return false
  }
  // 如果为null,则格式化当前时间
  date = new Date(date)

  if (date.toString() === 'Invalid Date') {
    return false
  }
  let ret
  const opt: any = {
    'y+': date.getFullYear().toString(),
    'm+': (date.getMonth() + 1).toString(),
    'd+': date.getDate().toString(),
    'h+': date.getHours().toString(),
    'M+': date.getMinutes().toString(),
    's+': date.getSeconds().toString(),
    // 有其他格式化字符需求可以继续添加，必须转化成字符串
  }
  for (const k in opt) {
    ret = new RegExp(`(${k})`).exec(fmt)
    if (ret) {
      fmt = fmt.replace(
        ret[1],
        ret[1].length == 1 ? opt[k] : opt[k].padStart(ret[1].length, '0')
      )
    }
  }
  return fmt
}

const getTagText = (props: any, row: any) => {
  const status = props.column.tagConfig.status
  if (status?.length > 0) {
    const datum = status.find(
      (item: any) => item.source === row[props.column.prop]
    )
    return datum?.text
  }
}

const getTagType = (props: any, row: any) => {
  const status = props.column.tagConfig.status
  if (status?.length > 0) {
    const datum = status.find(
      (item: any) => item.source === row[props.column.prop]
    )
    return datum?.type
  }
}

const getRowText = (row: any, props: any) => {
  const text = get(row, props)
  return text
}
</script>

<template>
  <el-table-column
    v-if="handleHidden"
    :key="props.column.prop"
    :prop="props.column.prop"
    :label="props.column.label"
    :width="props.column.width"
    :min-width="props.column.minWidth"
    :align="props.column.align"
    :sortable="props.column.sortable ? 'custom' : false"
    :sort-orders="props.column.sortable ? ['descending', 'ascending'] : []"
    :fixed="props.column.fixed ? props.column.fixed : false"
    :resizable="props.column.resizable"
    :show-overflow-tooltip="
      props.column.showOverflow ? props.column.showOverflow : false
    "
    :type="props.column.expand ? 'expand' : 'default'"
  >
    <template #default="scope">
      <template
        v-if="props.column.children && props.column.children.length > 0"
      >
        <el-form-base-table-column
          v-for="(item, index) in props.column.children"
          :key="index"
          :column="item"
          :columns-data="props.columnsData"
        />
      </template>
      <template v-else-if="!props.column.render">
        <template v-if="props.column.swithcConfig">
          <el-switch
            v-if="isFn(props.column.swithcConfig.isShow, scope.row)"
            v-model="scope.row[props.column.prop]"
            :disabled="isFn(props.column.swithcConfig.disabled, scope.row)"
            :before-change="
              props.column.swithcConfig.beforeChange?.bind(null, scope.row)
            "
            @change="props.column.swithcConfig.on(scope.row)"
          />
        </template>
        <template v-else-if="props.column.tagConfig">
          <el-tag
            v-if="isFn(props.column.tagConfig.isShow, scope.row)"
            :type="getTagType(props, scope.row)"
            @click="props.column.tagConfig.on(scope.row)"
            >{{ getTagText(props, scope.row) }}</el-tag
          >
          <!-- {{ getTagText(props, scope.row) }} -->
        </template>
        <template v-else-if="props.column.dateFormatter">
          {{ toFormat(props, scope.row) }}
        </template>
        <template v-else>
          <template v-if="props.column.renderTooltip">
            <el-tooltip
              :content="
                props.column.renderTooltip(scope.row, columnsData).toString()
              "
              placement="top"
              :open-delay="500"
              effect="dark"
            >
              <span class="el-form-base-tooltip">{{
                getRowText(scope.row, props.column.prop)
              }}</span>
            </el-tooltip>
          </template>
          <template v-else>
            <span
              :class="[getLink]"
              @click="handleClick(props.column, scope.row)"
              >{{ scope.row[props.column.prop] }}</span
            >
          </template>
        </template>
      </template>
      <form-base-table-render
        v-if="!props.column.children && props.column.render"
        :row="props.column"
        :scope="scope"
      />
      <form-base-table-expand-render
        v-if="!props.column.children && props.column.expand"
        :row="props.column"
        :scope="scope"
      />
    </template>
  </el-table-column>
</template>
